"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { ParseKeys } from "i18next";
import { subMinutes, subHours } from "date-fns";
import { useDateTimeRangeStore, CompareToOption, TargetSourceOption } from "@/shared/stores/useDateTimeRangeStore.ts";
import { SHIFT_DEFINITIONS, getShiftRange } from "@/shared/utils/shifts.ts";
import { useOrgTimeZone } from "@/shared/utils/date/org-time.ts";
import DateTimeCustomPicker, { DateTimeRangeValue } from "./date-time-custom-picker.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";
import { Button } from "@/ui/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/components/ui/tabs";
import { Label } from "@/ui/components/ui/label";
import { cn } from "@/ui/lib/utils";

const LAST_APPLIED_KEY = "date-time-range-last-applied";

const DYNAMIC_PRESETS = new Set([
  "last15min",
  "last30min",
  "last1hour",
  "last3hours",
  "last12hours",
  "last24hours",
]);

interface PresetDefinition {
  key: string;
  /** `common` key — presets are built outside the render, so the label is not. */
  labelKey: ParseKeys<"common">;
  from: Date;
  to: Date;
}

// The rolling "last N" presets are zone-independent — they end at `now` and span
// a fixed duration, so they resolve to the same instants everywhere. Only the
// SHIFT presets are wall-clock windows and need the location's zone.
const createPresets = (
  now: Date,
  customDraft: DateTimeRangeValue,
  timeZone: string,
): PresetDefinition[] => [
  { key: "last15min", labelKey: "dateRange.presets.last15min", from: subMinutes(now, 15), to: now },
  { key: "last30min", labelKey: "dateRange.presets.last30min", from: subMinutes(now, 30), to: now },
  { key: "last1hour", labelKey: "dateRange.presets.last1hour", from: subHours(now, 1), to: now },
  { key: "last3hours", labelKey: "dateRange.presets.last3hours", from: subHours(now, 3), to: now },
  { key: "last12hours", labelKey: "dateRange.presets.last12hours", from: subHours(now, 12), to: now },
  { key: "last24hours", labelKey: "dateRange.presets.last24hours", from: subHours(now, 24), to: now },
  ...SHIFT_DEFINITIONS.map((s) => {
    const r = getShiftRange(s, now, timeZone);
    return { key: s.key, labelKey: s.labelKey, from: r.from, to: r.to };
  }),
  {
    key: "custom",
    labelKey: "dateRange.presets.custom",
    from: customDraft.fromDate,
    to: customDraft.toDate,
  },
];

export default function DateTimeRangePicker() {
  const { t } = useTranslation("common");
  const {
    value,
    setValue,
    selectedPresetKey,
    setSelectedPresetKey,
    compareToEnabled,
    compareToOption,
    targetSourceEnabled,
    targetSourceOption,
    setCompareToEnabled,
    setCompareToOption,
    setTargetSourceEnabled,
    setTargetSourceOption,
  } = useDateTimeRangeStore();

  const [selectedLabelKey, setSelectedLabelKey] = useState<ParseKeys<"common">>(
    "dateRange.presets.selectPreset",
  );
  const [draft, setDraft] = useState<DateTimeRangeValue>({
    fromDate: value.fromDate,
    toDate: value.toDate,
  });
  const [draftPresetKey, setDraftPresetKey] = useState<string | null>(selectedPresetKey);
  const [draftCompareToOption, setDraftCompareToOption] = useState<CompareToOption>(compareToOption);
  const [draftTargetSourceOption, setDraftTargetSourceOption] = useState<TargetSourceOption>(targetSourceOption);
  const [activeTab, setActiveTab] = useState("date-range");
  const isProgrammaticChange = useRef(false);
  // The active LOCATION's zone — shifts and the custom fields are wall-clock
  // windows at the site, so they are built and displayed in it.
  const timeZone = useOrgTimeZone();

  const presets = useMemo(
    () => createPresets(new Date(), draft, timeZone),
    [draft, timeZone],
  );

  const handlePresetClick = (presetKey: string) => {
    // Resolve from the CURRENT instant, not the `[draft]`-memoized `presets`, so
    // a shift (and the "last N" presets) always lands on TODAY even if the picker
    // was opened earlier or on a previous day.
    const preset = createPresets(new Date(), draft, timeZone).find((p) => p.key === presetKey);
    if (!preset) return;

    if (preset.key === "custom") {
      setDraftPresetKey("custom");
      setSelectedLabelKey("dateRange.presets.custom");
      return;
    }

    // Mark that we're about to make a programmatic change
    isProgrammaticChange.current = true;

    // Update draft and labels for preset selection
    setDraftPresetKey(preset.key);
    setSelectedLabelKey(preset.labelKey);
    setDraft({ fromDate: preset.from, toDate: preset.to });
  };

  const handleDraftChange = (newDraft: DateTimeRangeValue) => {
    // Ignore if this change was triggered by preset selection
    if (isProgrammaticChange.current) {
      return;
    }

    // When user manually changes the date/time via pickers, switch to custom
    setDraft(newDraft);
    setDraftPresetKey("custom");
    setSelectedLabelKey("dateRange.presets.custom");
  };

  // Reset programmatic change flag after draft updates
  useEffect(() => {
    if (isProgrammaticChange.current) {
      // Reset flag after the component has re-rendered with new draft
      isProgrammaticChange.current = false;
    }
  }, [draft]);

  // Refresh dynamic presets on mount
  useEffect(() => {
    if (selectedPresetKey && DYNAMIC_PRESETS.has(selectedPresetKey)) {
      const now = new Date();
      const preset = createPresets(now, draft, timeZone).find((p) => p.key === selectedPresetKey);
      if (preset) {
        isProgrammaticChange.current = true;
        setDraft({ fromDate: preset.from, toDate: preset.to });
        setDraftPresetKey(selectedPresetKey);
        setSelectedLabelKey(preset.labelKey);
      }
    } else if (selectedPresetKey === "custom") {
      setSelectedLabelKey("dateRange.presets.custom");
    } else if (selectedPresetKey) {
      // A non-dynamic named preset — a shift. A shift always belongs to TODAY, so
      // refresh its draft to the current calendar day on open; a persisted shift
      // from a previous day would otherwise reopen (and re-apply) on the stale
      // date. Same treatment the dynamic "last N" presets get above.
      const now = new Date();
      const preset = createPresets(now, draft, timeZone).find((p) => p.key === selectedPresetKey);
      if (preset) {
        isProgrammaticChange.current = true;
        setDraft({ fromDate: preset.from, toDate: preset.to });
        setDraftPresetKey(selectedPresetKey);
        setSelectedLabelKey(preset.labelKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if draft differs from saved state
  const isDirty = useMemo(() => {
    const lastApplied = localStorage.getItem(LAST_APPLIED_KEY);
    const currentSnapshot = JSON.stringify({
      from: draft.fromDate.toISOString(),
      to: draft.toDate.toISOString(),
      preset: draftPresetKey,
      compareToOption: draftCompareToOption,
      targetSourceOption: draftTargetSourceOption,
    });
    const storeSnapshot = JSON.stringify({
      from: value.fromDate.toISOString(),
      to: value.toDate.toISOString(),
      preset: selectedPresetKey,
      compareToOption,
      targetSourceOption,
    });

    return lastApplied !== currentSnapshot && currentSnapshot !== storeSnapshot;
  }, [
    draft.fromDate,
    draft.toDate,
    draftPresetKey,
    draftCompareToOption,
    draftTargetSourceOption,
    value.fromDate,
    value.toDate,
    selectedPresetKey,
    compareToOption,
    targetSourceOption,
  ]);

  const handleSave = () => {
    setValue({ fromDate: draft.fromDate, toDate: draft.toDate });
    setSelectedPresetKey(draftPresetKey ?? null);
    setCompareToEnabled(draftCompareToOption !== null);
    setCompareToOption(draftCompareToOption);
    setTargetSourceEnabled(draftTargetSourceOption !== null);
    setTargetSourceOption(draftTargetSourceOption);

    const snapshot = JSON.stringify({
      from: draft.fromDate.toISOString(),
      to: draft.toDate.toISOString(),
      preset: draftPresetKey,
      compareToOption: draftCompareToOption,
      targetSourceOption: draftTargetSourceOption,
    });
    localStorage.setItem(LAST_APPLIED_KEY, snapshot);
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="date-range">{t("dateRange.tabs.dateRange")}</TabsTrigger>
          <TabsTrigger value="compare-to">{t("dateRange.tabs.compareTo")}</TabsTrigger>
          <TabsTrigger value="target-source">{t("dateRange.tabs.targetSource")}</TabsTrigger>
        </TabsList>

        {/* Date Range Tab */}
        <TabsContent value="date-range" className="space-y-4 mt-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                {t(selectedLabelKey)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-full">
              {presets.map((preset) => (
                <DropdownMenuItem
                  key={preset.key}
                  onClick={() => handlePresetClick(preset.key)}
                >
                  {t(preset.labelKey)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DateTimeCustomPicker value={draft} onChange={handleDraftChange} timeZone={timeZone} />
        </TabsContent>

        {/* Compare To Tab */}
        <TabsContent value="compare-to" className="space-y-4 mt-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t("dateRange.compare.label")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={draftCompareToOption === null ? "default" : "outline"}
                className={cn("w-full", draftCompareToOption === null && "bg-primary text-primary-foreground")}
                onClick={() => setDraftCompareToOption(null)}
              >
                {t("dateRange.compare.none")}
              </Button>
              <Button
                type="button"
                variant={draftCompareToOption === "previous-shift" ? "default" : "outline"}
                className="w-full"
                onClick={() => setDraftCompareToOption("previous-shift")}
              >
                {t("dateRange.compare.previousShift")}
              </Button>
              <Button
                type="button"
                variant={draftCompareToOption === "previous-day" ? "default" : "outline"}
                className="w-full"
                onClick={() => setDraftCompareToOption("previous-day")}
              >
                {t("dateRange.compare.previousDay")}
              </Button>
              <Button
                type="button"
                variant={draftCompareToOption === "avg-last-7-days" ? "default" : "outline"}
                className="w-full"
                onClick={() => setDraftCompareToOption("avg-last-7-days")}
              >
                {t("dateRange.compare.avgLast7Days")}
              </Button>
              <Button
                type="button"
                variant={draftCompareToOption === "previous-hour" ? "default" : "outline"}
                className="w-full col-span-2"
                onClick={() => setDraftCompareToOption("previous-hour")}
              >
                {t("dateRange.compare.previousHour")}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Target Source Tab */}
        <TabsContent value="target-source" className="space-y-4 mt-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t("dateRange.target.label")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={draftTargetSourceOption === null ? "default" : "outline"}
                className={cn("w-full", draftTargetSourceOption === null && "bg-primary text-primary-foreground")}
                onClick={() => setDraftTargetSourceOption(null)}
              >
                {t("dateRange.target.none")}
              </Button>
              <Button
                type="button"
                variant={draftTargetSourceOption === "plan" ? "default" : "outline"}
                className="w-full"
                onClick={() => setDraftTargetSourceOption("plan")}
              >
                {t("dateRange.target.plan")}
              </Button>
              <Button
                type="button"
                variant={draftTargetSourceOption === "historical-avg" ? "default" : "outline"}
                className="w-full"
                onClick={() => setDraftTargetSourceOption("historical-avg")}
              >
                {t("dateRange.target.historicalAvg")}
              </Button>
              <Button
                type="button"
                variant={draftTargetSourceOption === "machine-targets" ? "default" : "outline"}
                className="w-full"
                onClick={() => setDraftTargetSourceOption("machine-targets")}
              >
                {t("dateRange.target.machineTargets")}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Button variant="default" disabled={!isDirty} onClick={handleSave} className="w-full">
        {t("dateRange.save")}
      </Button>
    </div>
  );
}
